/**
 * Creative Loader Hook
 * Synchronizes creative selection with editor stores
 * Loads creative data (template, timeline, variables) when a creative is selected
 */

import { useEffect, useRef } from 'react';
import { useCreativeStore, useEditorStore, useTemplateStore, useAnimationStore } from '@/stores';
import { getFormatById } from '@/components/providers';
import type { Template } from '@/types/template';

export function useProjectLoader() {
  const currentCreativeId = useCreativeStore((s) => s.currentCreativeId);
  const creatives = useCreativeStore((s) => s.creatives);
  const isInitialized = useCreativeStore((s) => s.isInitialized);

  const { setFormat, setContent } = useEditorStore();
  const { loadTemplate, setVariableValues, clearTemplate } = useTemplateStore();
  const { setTimeline, clearTimeline } = useAnimationStore();

  // Track the last loaded creative to avoid reloading
  const lastLoadedCreativeId = useRef<string | null>(null);

  useEffect(() => {
    // Wait for store to be initialized
    if (!isInitialized) return;

    // Already loaded this creative
    if (currentCreativeId === lastLoadedCreativeId.current) return;

    // No creative selected - clear state
    if (!currentCreativeId) {
      lastLoadedCreativeId.current = null;
      clearTemplate();
      clearTimeline();
      return;
    }

    // Find the creative
    const creative = creatives.find((c) => c.id === currentCreativeId);
    if (!creative) {
      lastLoadedCreativeId.current = null;
      return;
    }

    // Load creative data into editor stores
    lastLoadedCreativeId.current = currentCreativeId;

    // Load format
    const format = getFormatById(creative.primaryFormatId);
    if (format) {
      setFormat(format);
    }

    // Load content
    setContent(creative.content);

    // Load template
    if (creative.templateData) {
      try {
        const template = JSON.parse(creative.templateData) as Template;
        loadTemplate(template);
      } catch (e) {
        console.error('Failed to parse template data:', e);
      }
    }

    // Load timeline
    if (creative.timelineData) {
      try {
        const timeline = JSON.parse(creative.timelineData);
        setTimeline(timeline);
      } catch (e) {
        console.error('Failed to parse timeline data:', e);
      }
    } else {
      clearTimeline();
    }

    // Load variable values
    if (creative.variableValues) {
      setVariableValues(creative.variableValues);
    } else {
      // Fallback to content-based values
      setVariableValues({
        headline: creative.content.headline,
        subhead: creative.content.subhead || '',
        cta: creative.content.cta,
      });
    }
  }, [
    currentCreativeId,
    creatives,
    isInitialized,
    setFormat,
    setContent,
    loadTemplate,
    setVariableValues,
    setTimeline,
    clearTemplate,
    clearTimeline,
  ]);
}

// Alias for new name
export const useCreativeLoader = useProjectLoader;
